import { useCallback, useEffect } from 'react'

import { invokeAndroidMethod } from './androidBridge'

export interface StartPaymentRequest {
  orderId: string
}

export interface CancelPaymentRequest {
  orderId: string
}

export interface PrintOrderRequest {
  orderId: string
}

export interface PrintOrderResult {
  message?: string
  [key: string]: unknown
}

export interface PrinterStatus {
  connected: boolean
  ready: boolean
  paperEmpty: boolean
  paperNearEmpty: boolean
  coverOpen: boolean
  hasError: boolean
}

interface UseTerminalOptions {
  onPaymentStatusUpdate?: (status: unknown) => void
}

declare global {
  interface Window {
    onPaymentStatusUpdate?: ((statusPayload: unknown) => void) | undefined
  }
}

const parseStatusPayload = (statusPayload: unknown) => {
  if (typeof statusPayload !== 'string') {
    return statusPayload
  }

  try {
    return JSON.parse(statusPayload) as unknown
  } catch {
    return statusPayload
  }
}

const parsePrinterStatus = (value: unknown): PrinterStatus => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Android.getPrinterStatus returned invalid payload.')
  }

  const status = value as Record<string, unknown>
  const connected = status.connected
  const ready = status.ready
  const paperEmpty = status.paperEmpty
  const paperNearEmpty = status.paperNearEmpty
  const coverOpen = status.coverOpen
  const hasError = status.hasError

  if (
    typeof connected !== 'boolean' ||
    typeof ready !== 'boolean' ||
    typeof paperEmpty !== 'boolean' ||
    typeof paperNearEmpty !== 'boolean' ||
    typeof coverOpen !== 'boolean' ||
    typeof hasError !== 'boolean'
  ) {
    throw new Error('Android.getPrinterStatus returned invalid payload.')
  }

  return { connected, ready, paperEmpty, paperNearEmpty, coverOpen, hasError }
}

const startPaymentFromAndroid = async (payload: StartPaymentRequest) => {
  await invokeAndroidMethod<unknown>('startPayment', payload)
}

const cancelPaymentFromAndroid = async (payload: CancelPaymentRequest) => {
  await invokeAndroidMethod<unknown>('cancelPayment', payload)
}

const getPrinterStatusFromAndroid = async () => {
  const result = await invokeAndroidMethod<unknown>('getPrinterStatus')
  return parsePrinterStatus(result)
}

const printOrderFromAndroid = (payload: PrintOrderRequest) => invokeAndroidMethod<PrintOrderResult>('printOrder', payload)

const printTestReceiptFromAndroid = () => invokeAndroidMethod<PrintOrderResult>('printTestReceipt')

const useTerminal = ({ onPaymentStatusUpdate }: UseTerminalOptions = {}) => {
  useEffect(() => {
    if (!onPaymentStatusUpdate) {
      return
    }

    console.log('[useTerminal] registering window.onPaymentStatusUpdate handler')
    const previousHandler = window.onPaymentStatusUpdate
    const statusHandler = (statusPayload: unknown) => {
      console.log('[useTerminal] received payment status update', statusPayload)
      onPaymentStatusUpdate(parseStatusPayload(statusPayload))
    }

    window.onPaymentStatusUpdate = statusHandler

    return () => {
      if (window.onPaymentStatusUpdate === statusHandler) {
        console.log('[useTerminal] restoring previous window.onPaymentStatusUpdate handler')
        window.onPaymentStatusUpdate = previousHandler
      }
    }
  }, [onPaymentStatusUpdate])

  const startPayment = useCallback((payload: StartPaymentRequest) => startPaymentFromAndroid(payload), [])
  const cancelPayment = useCallback((payload: CancelPaymentRequest) => cancelPaymentFromAndroid(payload), [])
  const printOrder = useCallback((payload: PrintOrderRequest) => printOrderFromAndroid(payload), [])
  const printTestReceipt = useCallback(() => printTestReceiptFromAndroid(), [])
  const getPrinterStatus = useCallback(() => getPrinterStatusFromAndroid(), [])

  return {
    startPayment,
    cancelPayment,
    printOrder,
    printTestReceipt,
    getPrinterStatus
  }
}

export default useTerminal
